import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaFileExport } from "react-icons/fa";
import { toast } from "react-toastify";
import Server from "../../Shared/Server";
import Spinner from "../../Shared/Spinner";
import BackButton from '../../Shared/BackButton'
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const XMonthsUnPaidDetail = () => {

  const [data, setData] = useState([]);
  const [title, setTitle] = useState('');
  const [columnDefs, setColumnDefs] = useState([]);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const gridRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch API data
  const fetchData = async (payload) => {

    setIsLoading(true);
    try {
      const response = await Server.get(`/un-paid/x-months-detail?year=${payload.year}&month=${payload.month}&x=${payload.x}&public_id=${payload.public_id}`);
      const res = response.data;
      setData(res.data);

      // Extract month keys from the "months" array.
      const monthsFromApi = res.months;

      // Build column definitions dynamically
      buildColumnDefs(monthsFromApi);

      // Compute totals for pinned bottom row.
      computePinnedTotals(res.data, monthsFromApi);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false)
      if(error?.response?.status === 401){
          navigate('/login')
      }else if (error?.response?.status === 403){
          toast.error(error?.response?.data?.detail || 'Forbidden');
          navigate('/');
      }else {
          toast.error(error?.response?.data?.detail || 'Failed to load X months unpaid details');
      }
    }
  };

  // Build column definitions dynamically using monthList
  const buildColumnDefs = (monthKeys) => {
    // Static columns:
    let staticCols = [
      {
        headerName: "#",
        field: "index",
        maxWidth: 70,
        sortable: true,
        filter: false,
        valueFormatter: (params) =>
          isNaN(params.node.rowIndex) ? "" : `${params.node.rowIndex + 1}`,
      },
      {
        headerName: "ID",
        field: "location_id",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Name",
        field: "full_name",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Contact",
        field: "contact_number",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Remark",
        field: "remark",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Previous Reading",
        field: "previous_reading",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Current Reading",
        field: "current_reading",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Difference",
        field: "difference",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "PBalance",
        field: "previous_balance",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total Bill",
        field: "total_debit",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total",
        field: "total",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
    ];

    // Dynamic month columns:
    const dynamicCols = monthKeys.map((month) => ({
      headerName: month,
      field: month,
      sortable: true,
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        const val = parseFloat(params.value);
        return isNaN(val) ? "0.00" : val.toFixed(2);
      },
    }));

    // Find the index of the "previous_balance" column
    const previousBalanceIndex = staticCols.findIndex(col => col.field === "previous_balance");

    // Combine the columns so that dynamicColumns appear right after "PreviousBalance column"
    const finalColumnDefs = [
        ...staticCols.slice(0, previousBalanceIndex + 1),
        ...dynamicCols,
        ...staticCols.slice(previousBalanceIndex + 1),
    ];

    setColumnDefs(finalColumnDefs);
  };

  // Compute totals for pinned bottom row based on data and monthList
  const computePinnedTotals = (dataRecords, monthKeys) => {
    // Initialize totals for static columns:
    let totals = {
      previous_balance: 0,
      total_debit: 0,
      total: 0,
    };

    // And for dynamic month columns:
    monthKeys.forEach((month) => {
      totals[month] = 0;
    });

    dataRecords.forEach((record) => {
      totals.previous_balance += parseFloat(record.previous_balance || 0);
      totals.total_debit += parseFloat(record.total_debit || 0);
      totals.total += parseFloat(record.total || 0);

      monthKeys.forEach((month) => {
        totals[month] += parseFloat(record[month] || 0);
      });
    });

    const formattedTotals = {};
    Object.keys(totals).forEach(key => {
        formattedTotals[key] = Number(totals[key]).toFixed(2);
    });

    formattedTotals.name = "Totals";

    setPinnedBottomRowData([formattedTotals]);
  };

  useEffect(() => {

    if (location.state){
        const {payload} = location.state
        const data = {
            year: payload.year,
            month: payload.month,
            x: payload.x,
            public_id: payload.public_id,
        }
        fetchData(data);
        setTitle(`${payload.name}, ${payload.x} Months Unpaid`);
    }
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      filterParams: {
        buttons: [],
      },
    };
  }, []);

  const onFirstDataRendered = (params) => {
    params.api.autoSizeAllColumns();
  }

  const onGridSizeChanged = (params) => {
    params.api.autoSizeAllColumns();
  }

    const gridApiRef = useRef(null);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onExportClick = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        } else {
            console.error("Grid API is not available yet!");
        }
    };

  const getRowStyle = (params) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
        color: "#000",
      };
    }
    return {};
  };

  if (isLoading) return <Spinner />;

  return (
        <>
        <div className="row">
            <div className="col-md-6">
                <BackButton
                    to="/x-months-un-paid"
                    state={{ year: location.state?.payload?.year, month: location.state?.payload?.month, x: location.state?.payload?.x, by: location.state?.payload?.by }}
                    label="Back to Summary"
                />
            </div>
            <div className="col-md-6">
                <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
            </div>
        </div>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            suppressMenuHide={true}
                            rowData={data}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  );
};

export default XMonthsUnPaidDetail;
